import {
  Component,
  OnInit,
  computed,
  inject,
  input,
  signal,
} from "@angular/core";
import { Router, RouterLink } from "@angular/router";
import { NbAlertModule, NbComponentStatus, NbIconModule } from "@nebular/theme";
import { EventService } from "app/@core/services/event.service";
import { Course, Event, EventFilter, EventLink } from "app/@core/types";
import { DayjsService } from "app/@core/utils/dayjs.service";
import { DateFormatPipe } from "../../pipes/date-format.pipe";
import { HighlightPipe } from "../../pipes/highlight.pipe";

@Component({
  standalone: true,
  selector: "ngx-course-event",
  styleUrls: ["./event.component.scss"],
  templateUrl: "./event.component.html",
  imports: [
    RouterLink,
    NbAlertModule,
    NbIconModule,
    HighlightPipe,
    DateFormatPipe,
  ],
})
export class EventComponent implements OnInit {
  private dayjs = inject(DayjsService);
  private router = inject(Router);
  private eventService = inject(EventService);

  title = input<string>();
  // @Input() events: Event[];
  course = input.required<Course>();
  filter = input.required<EventFilter>();
  outline? = input<NbComponentStatus>("basic");
  link? = input<EventLink>();
  searchField = input<string>();

  private additionalLimit = signal(0);

  basepath: string;

  ngOnInit() {
    this.basepath = this.course().slug == "aces" ? "/courses" : "/course";
    this.eventService.loadCourseEvents(this.course().id);
  }

  readonly filteredEvents = computed(() => {
    const rawData = [...this.eventService.allEvents()]; // Use spread to avoid mutating original
    const currentFilter = this.filter();
    const currentSearch = this.searchField();
    const limit = this.effectiveLimit();
    let result: Event[] = [];

    // Apply Category Filtering
    switch (currentFilter.key) {
      case "limit":
        result = rawData.filter((e) => this.dayjs.isAfter(e.enddate));
        break;
      case "upcoming":
        result = rawData.filter((e) => this.dayjs.isAfter(e.datetime));
        break;
      case "past":
        result = rawData
          .filter((e) => this.dayjs.isBefore(e.datetime) && !e.options.video)
          .sort((a, b) => this.dayjs.compareDesc(b.datetime, a.datetime));
        break;
      case "pastrecording":
        result = rawData
          .filter((e) => this.filterPastRecording(e))
          .sort((a, b) => this.dayjs.compareDesc(b.datetime, a.datetime));
        break;
      case "module":
        result = rawData.filter((e) => e.module_id === currentFilter.value);
        break;
      default:
        result = rawData;
    }
    //Apply Search (Replaces FilterByPipe for better performance)
    if (currentSearch) {
      result = result.filter((p) =>
        ["name", "topic", "event.options.location", "link.text"].some((key) =>
          String(p[key as keyof Event] ?? "")
            .toLowerCase()
            .includes(currentSearch),
        ),
      );
    }

    if (limit !== -1) {
      return result.slice(0, limit);
    }

    return result;
  });

  readonly effectiveLimit = computed(() => {
    const baseLimit = this.filter().limit;
    // If baseLimit is -1 (show all), we don't need to add anything
    if (baseLimit === -1) return -1;
    return baseLimit + this.additionalLimit();
  });

  filterPastRecording(event: Event) {
    if (typeof event.options.video === "object" && event.options.video !== null)
      return (
        this.dayjs.isBefore(event.datetime) && event.options.video.id != ""
      );
    return false;
  }

  loadMore() {
    this.additionalLimit.update((n) => n + 10);
  }

  viewEvent(id: Number): void {
    if (![11, 31].includes(this.course().id)) {
      this.router.navigate(["course", this.course().slug, "events", id]);
    }
  }
}
